"use client"

import { useRouter } from "next/navigation"
import {
  FilterValues,
  VerificationType,
  ExperienceLevel,
  WonShipmentsRange,
} from "@/lib/drivers/types"
import { toURLParam, fromURLFilterValue } from "@/lib/drivers/url-params"
import type { DriverFiltersProps, ServiceName } from "@/lib/drivers/types"
import ShipmentCategorySelect from "./categorySelect"

const VERIFICATION_DISPLAY_NAMES: Record<VerificationType, string> = {
  backgroundCheck: "Background Check",
  insurance: "Insurance",
  identity: "Identity",
  usdot: "USDOT",
  usda: "USDA",
  manual: "Manual",
}

const SHIPMENT_TYPE_DISPLAY_NAMES: Record<string, string> = {
  "Solo Rides (VIP)": "Solo Rides (VIP)",
  "Door to door delivery": "Door to Door Delivery",
  "GPS Tracking": "GPS Tracking",
  "Open trailer hauling": "Open Trailer Hauling",
  "Palletized transport": "Palletized Transport",
  "Large boat hauling": "Large Boat Hauling",
  "Small boat hauling": "Small Boat Hauling",
  "Oversized boat hauling": "Oversized Boat Hauling",
  "Wrapping protection": "Wrapping Protection",
  "Enclosed trailer hauling": "Enclosed Trailer Hauling",
}

export function DriverFilters({
  filterStats,
  searchParams,
  onFilterChange,
}: DriverFiltersProps) {
  const router = useRouter()

  const handleFilterChange = (type: keyof FilterValues, value: string) => {
    let updates: Partial<FilterValues> = {}
    switch (type) {
      case "verifications":
        updates.verifications = value
          ? (value.split(",").filter(Boolean) as VerificationType[])
          : []
        break
      case "shipmentTypes":
        updates.shipmentTypes = value
          ? (value.split(",").filter(Boolean) as ServiceName[])
          : []
        break
      case "rating":
        updates.rating = value ? Number(value) : null
        break
      case "experience":
        updates.experience = value ? (value as ExperienceLevel) : null
        break
      case "wonShipments":
        updates.wonShipments = value ? (value as WonShipmentsRange) : null
        break
      case "shipmentCategories":
        updates.shipmentCategories = value
        break
    }

    onFilterChange(updates)
  }

  const handleResetFilters = () => {
    router.replace("/drivers", { scroll: false })
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-medium mb-2">Verifications</h3>
        <div className="space-y-2">
          {Object.entries(filterStats.verifications).map(([key, count]) => (
            <label key={key} className="flex items-center">
              <input
                type="checkbox"
                className="mr-2"
                checked={searchParams.verifications
                  ?.split(",")
                  .filter(Boolean)
                  .map(fromURLFilterValue)
                  .includes(key)}
                onChange={(e) => {
                  const current =
                    searchParams.verifications?.split(",").filter(Boolean) || []
                  const updated = e.target.checked
                    ? [...current, toURLParam(key)]
                    : current.filter((v) => fromURLFilterValue(v) !== key)
                  handleFilterChange("verifications", updated.join(","))
                }}
              />
              <span>
                {VERIFICATION_DISPLAY_NAMES[key as VerificationType] || key}
              </span>
              <span className="text-gray-500 text-sm ml-1">({count})</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-medium mb-2">Average Rating</h3>
        <select
          className="w-full p-2 border rounded"
          value={searchParams.rating || ""}
          onChange={(e) => handleFilterChange("rating", e.target.value)}
        >
          <option value="">Any Rating</option>
          {[4, 3, 2, 1].map((rating) => (
            <option key={rating} value={rating}>
              {rating}+ Stars (
              {Object.entries(filterStats.ratings)
                .filter(([r]) => parseInt(r) >= rating)
                .reduce((sum, [_, count]) => sum + count, 0)}
              )
            </option>
          ))}
        </select>
      </div>

      <div>
        <h3 className="font-medium mb-2">Experience</h3>
        <select
          className="w-full p-2 border rounded"
          value={searchParams.experience || ""}
          onChange={(e) => handleFilterChange("experience", e.target.value)}
        >
          <option value="">Any Experience</option>
          <option value="new">
            Less than 6 months ({filterStats.experience.new})
          </option>
          <option value="intermediate">
            6 months to 2 years ({filterStats.experience.intermediate})
          </option>
          <option value="expert">
            More than 2 years ({filterStats.experience.expert})
          </option>
        </select>
      </div>

      <div>
        <h3 className="font-medium mb-2">Completed Shipments</h3>
        <select
          className="w-full p-2 border rounded"
          value={searchParams.wonShipments || ""}
          onChange={(e) => handleFilterChange("wonShipments", e.target.value)}
        >
          <option value="">Any Number</option>
          <option value="lessThan10">
            Less than 10 ({filterStats.wonShipments.lessThan10})
          </option>
          <option value="between10And50">
            10 to 50 ({filterStats.wonShipments.between10And50})
          </option>
          <option value="moreThan50">
            More than 50 ({filterStats.wonShipments.moreThan50})
          </option>
        </select>
      </div>

      <div>
        <h3 className="font-medium mb-2">Shipment Types</h3>
        <div className="space-y-2">
          {Object.entries(filterStats.shipmentTypes).map(([type, count]) => {
            return (
              <label key={type} className="flex items-center">
                <input
                  type="checkbox"
                  className="mr-2"
                  checked={searchParams.shipmentTypes
                    ?.split(",")
                    .filter(Boolean)
                    .map((v) => fromURLFilterValue(v))
                    .includes(type)}
                  onChange={(e) => {
                    const current =
                      searchParams.shipmentTypes?.split(",").filter(Boolean) ||
                      []
                    const updated = e.target.checked
                      ? [...current, type]
                      : current.filter((t) => fromURLFilterValue(t) !== type)
                    handleFilterChange("shipmentTypes", updated.join(","))
                  }}
                />
                <span>{SHIPMENT_TYPE_DISPLAY_NAMES[type] || type}</span>
                <span className="text-gray-500 text-sm ml-1">({count})</span>
              </label>
            )
          })}
        </div>
      </div>

      <ShipmentCategorySelect
        searchParams={searchParams}
        handleFilterChange={handleFilterChange}
      />
      <button
        onClick={handleResetFilters}
        className="w-full py-2 text-sm text-gray-600 hover:text-gray-900 border rounded"
      >
        Reset Filters
      </button>
    </div>
  )
}
